export default function Story() {
  return (
    <section id="story" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-amber-600 text-sm font-semibold tracking-widest uppercase mb-4 block" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Our Journey
          </span>
          <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            A Family Legacy of Love & Tradition
          </h3>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          <div className="relative h-96 md:h-[550px] rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src="https://public.readdy.ai/ai/img_res/f5cbf17113c50aaef04bb5853360fb3f.jpg" 
              alt="Family gathering during olive harvest in Tunisia"
              className="w-full h-full object-cover object-center"
            />
          </div>
          
          <div className="space-y-6">
            <h4 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              From Mahdia to America
            </h4>
            
            <p className="text-gray-700 text-lg leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              <strong>Carthaginian Treasure</strong> is brought to you by <strong>Rafika Mansour</strong>, a <strong>Tunisian-American woman</strong> who grew up in <strong>Mahdia</strong>, on the southern <strong>East coast of Tunisia</strong>. She participated every year with her family in the annual <strong>olive oil harvest</strong>.
            </p>
            
            <p className="text-gray-700 text-lg leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              It was more than a harvest — it is <strong>bringing family and workers together</strong>, it is <strong>love</strong>, it is <strong>sharing</strong>, it is bringing goodness to you.
            </p>
            
            <div className="bg-amber-50 rounded-xl p-6 border-l-4 border-amber-500">
              <p className="text-gray-800 leading-relaxed italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }}>
                "Every year, my family would prepare favorite meals and gifts in anticipation of the harvest. The workers who resided near our farms became like extended family. This tradition instilled in me a strong sense of joy, authenticity, and meticulousness in handling the olives."
              </p>
              <p className="text-gray-600 text-sm mt-3 text-right" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                — Rafika Mansour
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-50 to-amber-50/30 rounded-2xl p-12 mb-12">
          <h4 className="text-3xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Our Mission & Values
          </h4>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <h5 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                <i className="ri-heart-3-line text-amber-600 text-2xl"></i>
                Family-Owned Excellence
              </h5>
              <p className="text-gray-700 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <strong>Carthaginian Treasure</strong> is a premium brand that brings the purest form of <strong>wild cold-pressed Mediterranean olive oil</strong> from local wild olive trees on the East coast of Tunisia to the US market. Our founder's personal connection to the <strong>olive oil harvesting tradition</strong> and the desire to share the joy and authenticity with American consumers drives our mission.
              </p>
            </div>
            
            <div>
              <h5 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                <i className="ri-shield-star-line text-amber-600 text-2xl"></i>
                Quality & Authenticity
              </h5>
              <p className="text-gray-700 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                We are committed to importing the highest quality and purest <strong>wild olive oil</strong>, sourced from the same local farmers the founder grew up with, and ensuring compliance and proper grading through rigorous testing and certification processes.
              </p>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-8 shadow-md">
            <h5 className="text-2xl font-bold text-gray-900 mb-4 text-center" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              What Makes Us Unique
            </h5>
            <p className="text-gray-700 leading-relaxed text-center max-w-4xl mx-auto" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              <strong>Carthaginian Treasure extra-virgin olive oil</strong> stands out from other <strong>olive oil brands</strong> due to its unique combination of factors. It is sourced from <strong>local wild farms</strong> on the East coast of Tunisia, ensuring the purest form of <strong>wild cold-pressed Mediterranean olive oil</strong>. Driven by a deep-rooted <strong>family tradition</strong> and personal connection to <strong>olive oil harvesting</strong>, we bring an authentic and joyful experience to consumers. Our commitment to importing the highest quality and purest <strong>wild olive oil</strong>, along with rigorous testing and certification, sets us apart in terms of quality and reliability.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-map-pin-line text-amber-600 text-2xl"></i>
            </div>
            <h5 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Single Origin
            </h5>
            <p className="text-gray-600 text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              From one region in Tunisia to your table
            </p>
          </div>
          
          <div className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-heart-line text-amber-600 text-2xl"></i>
            </div>
            <h5 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Direct Relationships
            </h5>
            <p className="text-gray-600 text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Working with the same farmers for generations
            </p>
          </div>
          
          <div className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-award-line text-amber-600 text-2xl"></i>
            </div>
            <h5 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Certified Quality
            </h5>
            <p className="text-gray-600 text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Rigorously tested and certified
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
